import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const CustomerPasswordsTab = ({ setMessage, setError }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Track which customer is being edited
    const [editingId, setEditingId] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const loadCustomers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                page: '1',
                limit: '5000',
                status_filter: 'active',
            });
            if (search.trim()) {
                params.append('search', search.trim());
            }
            const response = await http.get(
                `/admin/customers/enhanced?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCustomers(response.data.data.customers || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, []);

    // Debounced search
    useEffect(() => {
        const timeout = setTimeout(() => {
            loadCustomers();
        }, 500);
        return () => clearTimeout(timeout);
    }, [search]);

    const handleResetPassword = async (customerId, customerName) => {
        if (!newPassword) {
            setError('Please enter a new password');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            await http.post(
                `/admin/customers/${customerId}/reset-password`,
                { newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage(`Password updated for ${customerName}`);
            setEditingId(null);
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password');
        } finally {
            setSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setNewPassword('');
        setConfirmPassword('');
    };

    return (
        <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800">Customer Passwords</h2>
            <p className="text-sm text-gray-500">
                Search for a customer and update their password. Passwords are encrypted and cannot be viewed.
            </p>

            {/* Search */}
            <div className="relative">
                <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>

            {/* Customer List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
            ) : customers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No customers found</div>
            ) : (
                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {customers.map((c) => (
                                <React.Fragment key={c.customer_id}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                            {c.first_name} {c.last_name}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{c.email}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{c.phone || '-'}</td>
                                        <td className="px-6 py-4 text-right">
                                            {editingId === c.customer_id ? (
                                                <button
                                                    onClick={cancelEdit}
                                                    className="text-sm text-gray-500 hover:text-gray-700"
                                                >
                                                    Cancel
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => { cancelEdit(); setEditingId(c.customer_id); }}
                                                    className="text-sm bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md transition-colors"
                                                >
                                                    Change Password
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {editingId === c.customer_id && (
                                        <tr className="bg-orange-50">
                                            <td colSpan={4} className="px-6 py-4">
                                                <div className="flex flex-wrap items-end gap-4">
                                                    <div className="flex-1 min-w-[200px]">
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1">New Password</label>
                                                        <input
                                                            type="password"
                                                            value={newPassword}
                                                            onChange={(e) => setNewPassword(e.target.value)}
                                                            placeholder="Min 8 characters"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 text-sm"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-[200px]">
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm Password</label>
                                                        <input
                                                            type="password"
                                                            value={confirmPassword}
                                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                                            placeholder="Re-enter password"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 text-sm"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => handleResetPassword(c.customer_id, `${c.first_name} ${c.last_name}`)}
                                                        disabled={saving}
                                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
                                                    >
                                                        {saving ? 'Saving...' : 'Update Password'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CustomerPasswordsTab;
